import { useCallback, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { Bell, CalendarClock, Check, Send, X as XIcon } from "lucide-react";
import { color, radius, space, typography } from "@tokens/index";
import { Button, Popover, Text, TextInput, useViewport } from "../../primitives";
import { classifyTaskStatus, taskStatusLabel, type TaskStatus } from "../taskStatus";
import type { WorkItem } from "../../../experience-shell/workflows";
import {
  SNOOZE_QUICK_OPTIONS,
  formatRelativeTime,
  isSnoozeTargetValid,
  snoozeBounds,
} from "./formatters";
import { ActionButton, StatusPill } from "./shared";
import { hairline } from "./expandMotion";
import { Collapse } from "./Collapse";

export function CompletionMeta({
  task,
  status,
}: {
  readonly task: WorkItem;
  readonly status: TaskStatus;
}): ReactElement | null {
  if (task.completedAt === null) return null;

  const completedBy = task.completedByDisplayName ?? task.completedBy ?? "Unknown";
  const hasApprovalContract = task.requestedByQueueId != null;
  const approverName = task.approvedByDisplayName ?? task.approvedBy ?? "approver";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <Text role="caption" size="xs" tone="subtle" truncate>
        Completed by {completedBy} · {formatRelativeTime(task.completedAt)}
      </Text>
      {hasApprovalContract && task.approvedAt !== null && (
        <Text role="caption" size="xs" tone="subtle" truncate>
          Approved by {approverName} · {formatRelativeTime(task.approvedAt)}
        </Text>
      )}
      {hasApprovalContract && task.approvedAt === null && status === "pending-approval" && (
        <Text role="caption" size="xs" tone="muted" truncate>
          {task.requestedByQueueName != null && task.requestedByQueueName.length > 0
            ? `Awaiting approval from ${task.requestedByQueueName}`
            : "Awaiting approval"}
        </Text>
      )}
    </div>
  );
}

/**
 * Snooze content: four quick chips and a custom date-time input. Hosted
 * inline on `medium` and up, in a bottom sheet on `compact`.
 */
export function SnoozePicker({
  task,
  label,
  disabled,
  onSnooze,
  onClose,
}: {
  readonly task: WorkItem;
  /** "Snooze" or "Reschedule". Drives the heading and the labels. */
  readonly label: string;
  readonly disabled: boolean;
  readonly onSnooze: (newDueDateIso: string) => void;
  readonly onClose: () => void;
}): ReactElement {
  const [customValue, setCustomValue] = useState("");
  const bounds = useMemo(() => snoozeBounds(new Date()), []);
  const customSubmittable = useMemo(() => isSnoozeTargetValid(customValue, Date.now()), [customValue]);

  const handleMinutes = useCallback(
    (minutes: number) => {
      onSnooze(new Date(Date.now() + minutes * 60_000).toISOString());
    },
    [onSnooze],
  );

  const handleCustomSubmit = useCallback(() => {
    if (!customSubmittable) return;
    onSnooze(new Date(customValue).toISOString());
  }, [customSubmittable, customValue, onSnooze]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: space[3],
        padding: space[3],
        background: color.bg.surface2,
        border: hairline,
        borderRadius: radius.lg,
        minWidth: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Text role="caps" size="xs" tone="muted">
        {label} until
      </Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: space[2] }}>
        {SNOOZE_QUICK_OPTIONS.map((opt) => (
          <Button
            key={opt.label}
            variant="soft"
            size="md"
            disabled={disabled}
            aria-label={`${label} task ${task.id} for ${opt.label}`}
            onClick={() => handleMinutes(opt.minutes)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: space[2], alignItems: "center" }}>
        <input
          type="datetime-local"
          value={customValue}
          min={bounds.min}
          max={bounds.max}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCustomSubmit();
            }
          }}
          disabled={disabled}
          aria-label="Custom snooze datetime"
          style={{
            appearance: "auto",
            flex: 1,
            minWidth: 160,
            height: 40,
            padding: `0 ${space[3]}`,
            borderRadius: radius.md,
            border: hairline,
            background: color.bg.surface,
            color: color.fg.default,
            fontFamily: typography.family.system,
            fontSize: typography.scale.sm,
          }}
        />
        <Button
          variant="primary"
          size="md"
          disabled={disabled || !customSubmittable}
          aria-label="Apply custom snooze"
          onClick={handleCustomSubmit}
        >
          Apply
        </Button>
        <Button variant="ghost" size="md" disabled={disabled} aria-label="Close snooze picker" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function TaskActions({
  task,
  now,
  canApprove,
  isCompleting,
  isApproving,
  isRejecting,
  isSnoozing,
  onComplete,
  onApprove,
  onReject,
  onSnooze,
}: {
  readonly task: WorkItem;
  readonly now: Date;
  readonly canApprove: boolean;
  readonly isCompleting: boolean;
  readonly isApproving: boolean;
  readonly isRejecting: boolean;
  readonly isSnoozing: boolean;
  readonly onComplete: (id: string) => void;
  readonly onApprove: ((id: string) => void) | undefined;
  readonly onReject: ((id: string, comment: string) => void) | undefined;
  readonly onSnooze: ((id: string, newDueDateIso: string) => void) | undefined;
}): ReactElement {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozeAnchorRef = useRef<HTMLDivElement>(null);
  const { isCompact } = useViewport();
  const status = classifyTaskStatus(task, now);
  const busy = isCompleting || isApproving || isRejecting || isSnoozing;

  const row = { display: "flex", flexWrap: "wrap", gap: space[2], alignItems: "center" } as const;
  const stack = { display: "flex", flexDirection: "column", gap: space[2], minWidth: 0 } as const;

  const confirmRejection = useCallback(() => {
    if (onReject === undefined) return;
    const comment = reason.trim();
    if (comment.length === 0 || busy) return;
    onReject(task.id, comment);
    setReason("");
    setRejecting(false);
  }, [onReject, reason, busy, task.id]);

  if (status === "open" || status === "pending") {
    // A task that is not yet due is rescheduled. A due or undated task is snoozed.
    const isPending = status === "pending";
    const deferLabel = isPending ? "Reschedule" : "Snooze";
    const deferIcon = isPending ? (
      <CalendarClock size={14} strokeWidth={2.5} aria-hidden="true" />
    ) : (
      <Bell size={14} strokeWidth={2.5} aria-hidden="true" />
    );
    const picker =
      onSnooze !== undefined ? (
        <SnoozePicker
          task={task}
          label={deferLabel}
          disabled={busy}
          onSnooze={(iso) => {
            onSnooze(task.id, iso);
            setSnoozeOpen(false);
          }}
          onClose={() => setSnoozeOpen(false)}
        />
      ) : null;
    return (
      <div style={stack}>
        <div ref={snoozeAnchorRef} style={row}>
          <ActionButton
            label="Complete"
            icon={<Check size={14} strokeWidth={2.5} aria-hidden="true" />}
            tone="primary"
            disabled={busy}
            busy={isCompleting}
            onClick={() => onComplete(task.id)}
          />
          {onSnooze !== undefined && (
            <ActionButton
              label={deferLabel}
              icon={deferIcon}
              tone="secondary"
              disabled={busy}
              busy={isSnoozing}
              onClick={() => setSnoozeOpen((prev) => !prev)}
            />
          )}
        </div>
        {picker !== null && isCompact && (
          <Popover
            open={snoozeOpen}
            onClose={() => setSnoozeOpen(false)}
            anchorRef={snoozeAnchorRef}
            role="dialog"
            sheetOnCompact
          >
            {picker}
          </Popover>
        )}
        {picker !== null && !isCompact && <Collapse open={snoozeOpen}>{picker}</Collapse>}
      </div>
    );
  }

  if (status === "pending-approval" && canApprove && onApprove !== undefined) {
    return (
      <div style={stack}>
        <div style={row}>
          <ActionButton
            label="Approve"
            icon={<Check size={14} strokeWidth={2.5} aria-hidden="true" />}
            tone="primary"
            disabled={busy}
            busy={isApproving}
            onClick={() => onApprove(task.id)}
          />
          {onReject !== undefined && (
            <ActionButton
              label="Reject"
              icon={<XIcon size={14} strokeWidth={2.5} aria-hidden="true" />}
              tone="dangerOutline"
              disabled={busy}
              onClick={() => setRejecting((v) => !v)}
            />
          )}
        </div>
        {onReject !== undefined && (
          <Collapse open={rejecting}>
            <div style={{ ...stack, paddingTop: space[1] }}>
              <TextInput
                autoFocus
                placeholder="Reason for rejection…"
                value={reason}
                fullWidth
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    confirmRejection();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setRejecting(false);
                  }
                }}
                aria-label="Rejection reason"
              />
              <ActionButton
                label="Confirm rejection"
                icon={<Send size={14} strokeWidth={2.5} aria-hidden="true" />}
                tone="danger"
                disabled={busy || reason.trim().length === 0}
                busy={isRejecting}
                onClick={confirmRejection}
              />
            </div>
          </Collapse>
        )}
        <CompletionMeta task={task} status={status} />
      </div>
    );
  }

  // "pending-approval" seen by a non-approver, or "completed".
  const statusColor = status === "completed" ? color.accent.success : color.accent.warning;
  return (
    <div style={stack}>
      <div style={row}>
        <StatusPill label={taskStatusLabel(status)} tone={statusColor} />
      </div>
      <CompletionMeta task={task} status={status} />
    </div>
  );
}
